// src/hooks/useTemplateDxfLoader.js
// DXF parse hook - Redux dxfSlice'a dispatch ETMEZ (izole)
import { useCallback } from 'react';
import DxfParser from 'dxf-parser';
import { getHatchFromDXF, getEntityTransparencyMap, getAttribsFromDXF, getLayersFromDXF, getOleFramesFromDXF } from '../modules/dxf';

export function useTemplateDxfLoader() {
  const loadTemplateDxf = useCallback((file, onSuccess, onError) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const rawText = e.target.result;
      const parser = new DxfParser();

      try {
        const dxf = parser.parseSync(rawText);
        const dxfLines = rawText.split('\n').map(line => line.trim());

        // HATCH entities
        const hatchEntities = getHatchFromDXF(dxfLines);
        if (Array.isArray(hatchEntities.hatch)) {
          dxf.entities = [...dxf.entities, ...hatchEntities.hatch];
        }

        // Transparency
        const transparencyMap = getEntityTransparencyMap(dxfLines);
        dxf.entities = dxf.entities.map(entity => {
          const handleKey = entity.handle?.toUpperCase();
          if (handleKey && transparencyMap[handleKey] !== undefined) {
            return { ...entity, transparency: transparencyMap[handleKey] };
          }
          return entity;
        });

        // OLE2FRAME (dxf-parser bunları parse etmez; binary'siyle çıkar)
        const oleEntities = getOleFramesFromDXF(dxfLines);
        if (oleEntities.length > 0) {
          dxf.entities = [...dxf.entities, ...oleEntities];
        }

        // ATTRIB
        const attribsByInsert = getAttribsFromDXF(dxfLines);
        dxf.entities = dxf.entities.map(entity => {
          if (entity.type === 'INSERT') {
            const handleKey = entity.handle?.toUpperCase();
            if (handleKey && attribsByInsert[handleKey]) {
              return { ...entity, attribs: attribsByInsert[handleKey] };
            }
          }
          return entity;
        });

        // Layers
        const parsedLayers = dxf.tables?.layer?.layers;
        let layerArray = Array.isArray(parsedLayers)
          ? parsedLayers
          : parsedLayers ? Object.values(parsedLayers) : [];

        const enhancedLayers = getLayersFromDXF(dxfLines);
        layerArray = layerArray.map(layer => {
          const enhanced = enhancedLayers[layer.name];
          if (enhanced) {
            return {
              ...layer,
              onOff: enhanced.onOff,
              locked: enhanced.locked,
              frozen: enhanced.frozen,
              lineType: enhanced.lineType,
              lineWeight: enhanced.lineWeight,
              transparency: enhanced.transparency ?? 0,
            };
          }
          return layer;
        });

        // Text entity'lerini cikar (text_0, text_1, mtext_0, mtext_1, ...)
        const textEntities = [];
        let textIdx = 0;
        let mtextIdx = 0;
        dxf.entities.forEach(entity => {
          if (entity.type === 'TEXT') {
            textEntities.push({
              key: `text_${textIdx++}`,
              type: 'text',
              text: entity.text || '',
              position: entity.startPoint
                ? { x: entity.startPoint.x, y: entity.startPoint.y }
                : { x: 0, y: 0 },
            });
          } else if (entity.type === 'MTEXT') {
            textEntities.push({
              key: `mtext_${mtextIdx++}`,
              type: 'mtext',
              text: entity.text || '',
              position: entity.position
                ? { x: entity.position.x, y: entity.position.y }
                : { x: 0, y: 0 },
            });
          }
        });

        if (onSuccess) {
          onSuccess({
            dxfData: dxf,
            dxfRawText: rawText,
            layers: layerArray,
            textEntities,
          });
        }
      } catch (err) {
        console.error('[useTemplateDxfLoader] DXF parse hatasi:', err);
        if (onError) onError(err);
      }
    };

    reader.readAsText(file);
  }, []);

  return { loadTemplateDxf };
}
